import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { RESIZE_HANDLE_ID, RESIZE_HANDLE_WIDTH } from '../../constants/resize-handle.constants';
import { TableColumnDefUIDirective } from '../../directives/table-column-def.directive';
import { IconsInitConfigType } from '../../../../../constants';
import { nextSortDirection, TableSortDirectionType } from '../../models/table-sort.model';
import { TableResizeUIService } from '../../services/table-resize.service';
import { TableSortUIService } from '../../services/table-sort.service';
import { TableColumnResizeUIDirective } from '../../directives/table-column-resize.directive';
import { IconUIComponent } from '../../../../atoms/icon/icon.component';
import { TableActiveSortPipe } from '../../pipes/table-active-sort.pipe';

@Component({
  selector: 'fr8bot-ui-table-header-cell',
  host: { '[class.table-menu-column]': 'column.isMenu()' },
  templateUrl: './table-header-cell.component.html',
  styleUrl: './table-header-cell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconUIComponent, TableActiveSortPipe],
})
export class TableHeaderCellUIComponent {
  private document = inject<Document>(DOCUMENT);
  private cell = inject(ElementRef, { host: true });
  private resizeService = inject(TableResizeUIService);
  protected sortService = inject(TableSortUIService);
  protected column = inject<TableColumnDefUIDirective<unknown>>(TableColumnDefUIDirective);
  private resizeDirective = inject(TableColumnResizeUIDirective, { host: true, optional: true });

  readonly icon = input<IconsInitConfigType | null>(null);
  readonly withSorting = input(false);

  readonly content = viewChild<ElementRef<HTMLElement>>('cellContent');

  public readonly RESIZE_HANDLE_WIDTH = RESIZE_HANDLE_WIDTH;
  public readonly RESIZE_HANDLE_ID = RESIZE_HANDLE_ID;

  constructor() {
    // After render, not in ngAfterViewInit: a layout read mid-change-detection forces a style
    // recalc before the rest of the page is bound, and its transitions play on navigation.
    afterNextRender(() => this.registerColumnWidth());
  }

  private registerColumnWidth(): void {
    if (!this.resizeDirective && !this.column.rowName()) {
      // ORDER IS IMPORTANT HERE
      this.resizeService.setInitialColumnWidth(this.column.name(), this.getMinimumColumnWidth());
      this.resizeService.markColumnAsReady(this.column.name());
    }

    if (this.resizeDirective && !this.column.rowName()) {
      this.resizeService.setInitialColumnWidth(this.column.name(), this.getMinimumColumnWidth());
      // RESIZABLE COLUMNS BECOME READY ONLY AFTER resizeDirective INITIALIZATION. markColumnAsReady CALL IS IN DIRECTIVE
    }
  }

  public sortBy(key: string | null, sortDirection: TableSortDirectionType): void {
    if (key) {
      const currentSortOrder = this.sortService.current();
      const direction =
        currentSortOrder?.key === key && currentSortOrder?.direction === sortDirection
          ? undefined
          : sortDirection; // TODO: handle undefined scenario later #00000

      this.sortService.setSortOrder({ key, direction });
    }
  }

  /** The column title sorts too, not only the arrows next to it. */
  public cycleSort(key: string | null): void {
    if (key) {
      this.sortService.setSortOrder({
        key,
        direction: nextSortDirection(this.sortService.current(), key),
      });
    }
  }

  private getMinimumColumnWidth(): number {
    const contentElement = this.content()?.nativeElement;

    if (contentElement) {
      const [paddingLeft, paddingRight] = this.getCellPaddings();
      const minWidth = this.getCellMinWidth();

      if (minWidth) {
        return contentElement.clientWidth + minWidth + RESIZE_HANDLE_WIDTH;
      }

      return contentElement.clientWidth + paddingLeft + paddingRight + RESIZE_HANDLE_WIDTH;
    }

    return 0;
  }

  private getCellPaddings(): [number, number] {
    const cellElement = this.cell?.nativeElement as HTMLElement;

    if (this.document.defaultView && cellElement) {
      const { paddingLeft, paddingRight } = this.document.defaultView.getComputedStyle(cellElement);
      return [parseInt(paddingLeft), parseInt(paddingRight)];
    }

    return [0, 0];
  }

  private getCellMinWidth(): number {
    const cellElement = this.cell?.nativeElement as HTMLElement;

    if (this.document.defaultView && cellElement) {
      const { minWidth } = this.document.defaultView.getComputedStyle(cellElement);

      return parseInt(minWidth) || 0;
    }

    return 0;
  }
}
